import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
// Temapaletterna MÅSTE importeras efter App.css. Mörka temat bor i :root där
// och har samma specificitet som temablocken, så källordningen avgör vem som
// vinner — före App.css skulle inget tema slå igenom.
import './themes/index.css';
// Flair-trappan renderas i topplistor, på profilen och i butiken — global
// import så inget renderställe kan glömma den och visa en köpt stil ostilad.
import './components/shop/FlairStyles.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import UserMenu from './components/auth/UserMenu';
import AuthCallback from './components/auth/AuthCallback';
import LoginPage from './components/auth/LoginPage';
import PendingApproval from './components/auth/PendingApproval';
import { useLiveMatches } from './components/live/LiveMatch';
import useQueueWebSocket from './hooks/useQueueWebSocket';
import useActiveMatch from './hooks/useActiveMatch';
import usePermissions from './hooks/usePermissions';
import useCanSuggestMap from './hooks/useCanSuggestMap';
import useOpenSlotsCount from './hooks/useOpenSlotsCount';
import DocumentTitle from './hooks/useDocumentTitle';
import LiveMatch from './components/live/LiveMatch';
import PlayerProfile from './components/players/PlayerProfile';
import PlayersList from './components/players/PlayersList';
import MatchDetail from './components/matches/MatchDetail';
import MatchesList from './components/matches/MatchesList';
import HighlightsList from './components/highlights/HighlightsList';
import HeadToHead from './components/players/HeadToHead';
import Leaderboard from './components/leaderboard/Leaderboard';
import StatLeaderboards from './components/leaderboard/StatLeaderboards';
import PredictionLeaderboardCard from './components/leaderboard/PredictionLeaderboardCard';
import EloLeaderboardCard from './components/elo/EloLeaderboardCard';
import Queue from './components/queue/Queue';
import CreateMatch from './components/match/CreateMatch';
import OpenSlots from './components/openslots/OpenSlots';
import VetoDemo from './components/veto/VetoDemo';
import QueueDemo from './components/queue/QueueDemo';
import CreateMatchDemo from './components/match/CreateMatchDemo';
import VetoFlow from './components/veto/VetoFlow';
import PlayAgainBanner from './components/queue/PlayAgainBanner';
import AcceptMatchBanner from './components/queue/AcceptMatchBanner';
import ActiveMatchBanner from './components/match/ActiveMatchBanner';
import { NotificationProvider } from './contexts/NotificationContext';
import { MatchAcceptModalProvider } from './contexts/MatchAcceptModalContext';
import { SettingsProvider } from './contexts/SettingsContext';
import NotificationToast from './components/notifications/NotificationToast';
import NotificationBell from './components/notifications/NotificationBell';
import WalletChip from './components/common/WalletChip';
import Profile from './components/settings/Profile';
import Dashboard from './components/dashboard/Dashboard';
import MapStats from './components/maps/MapStats';
import SuggestMap from './components/maps/SuggestMap';
import SeasonOverview from './components/season/SeasonOverview';
import MedalsPage from './components/achievements/MedalsPage';
import { API_URL } from './utils/api';
import { isHybridMode } from './utils/config';
import ErrorBoundary from './components/common/ErrorBoundary';
import FeatureBoundary from './components/common/FeatureBoundary';
import CommandPalette from './components/common/CommandPalette';
import usePresence from './hooks/usePresence';
import OnlineSidebar from './components/presence/OnlineSidebar';
import ThemePreviewBanner from './components/common/ThemePreviewBanner';
import InstallPrompt from './components/pwa/InstallPrompt';
import AdminLayout from './components/admin/AdminLayout';
import TournamentList from './components/tournaments/TournamentList';
import TournamentDetail from './components/tournaments/TournamentDetail';
import NewsPage from './components/news/NewsPage';
import ShopPage from './components/shop/ShopPage';
import MarketplacePage from './components/marketplace/MarketplacePage';
import NewsDetail from './components/news/NewsDetail';
import StatusNoticeBanner from './components/status/StatusNoticeBanner';
import LeagueList from './components/leagues/LeagueList';
import LeagueDetail from './components/leagues/LeagueDetail';
import LeagueTeams from './components/leagues/LeagueTeams';
import LeagueTeamDetail from './components/leagues/LeagueTeamDetail';
import CreateLeagueTeam from './components/leagues/CreateLeagueTeam';
import MyLeagues from './components/leagues/MyLeagues';
import OverwatchDashboard from './components/overwatch/OverwatchDashboard';
import OverwatchReport from './components/overwatch/OverwatchReport';
import OverwatchPlayer from './components/overwatch/OverwatchPlayer';
import OverwatchAdmin from './components/overwatch/OverwatchAdmin';
import useOverwatchAccess from './hooks/useOverwatchAccess';
import ChatWidget from './components/chat/ChatWidget';

function LeaderboardsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleSelectPlayer = (steamid64) => navigate(`/stats/players/${steamid64}`);
  return (
    <div className="leaderboards-page">
      <div className="leaderboard-title-row">
        <h1 className="page-title">{t('elo.leaderboardsTitle')}</h1>
        <span className="leaderboard-info-hint">{t('elo.qualifyHint')}</span>
      </div>
      <div className="leaderboards-grid">
        <EloLeaderboardCard onSelectPlayer={handleSelectPlayer} />
        <Leaderboard title="K/D Ratio" statType="kd" icon="⚔️" onSelectPlayer={handleSelectPlayer} />
        <Leaderboard title="Totala Kills" statType="kills" icon="💀" onSelectPlayer={handleSelectPlayer} />
        <Leaderboard title="Headshot %" statType="headshots" icon="🎯" onSelectPlayer={handleSelectPlayer} />
        <Leaderboard title="ADR" statType="adr" icon="💥" onSelectPlayer={handleSelectPlayer} />
        <Leaderboard title="Clutches" statType="clutches" icon="🏆" onSelectPlayer={handleSelectPlayer} />
        <Leaderboard title="Entry Frags" statType="entries" icon="⚡" onSelectPlayer={handleSelectPlayer} />
        <Leaderboard title="Rating" statType="rating" icon="📊" onSelectPlayer={handleSelectPlayer} />
        <PredictionLeaderboardCard onSelectPlayer={handleSelectPlayer} />
      </div>
      <StatLeaderboards onSelectPlayer={handleSelectPlayer} />
    </div>
  );
}

function PlayersPage() {
  const navigate = useNavigate();
  return (
    <PlayersList
      onSelectPlayer={(id) => navigate(`/stats/players/${id}`)}
      onSelectH2H={(a, b) => navigate(`/stats/h2h/${a}/${b}`)}
    />
  );
}

function PlayerProfilePage() {
  const navigate = useNavigate();
  const { steamid64 } = useParams();
  return (
    <PlayerProfile
      steamid64={steamid64}
      onBack={() => navigate(-1)}
      onSelectMatch={(id) => navigate(`/stats/matches/${id}`)}
      onSelectH2H={(a, b) => navigate(`/stats/h2h/${a}/${b}`)}
    />
  );
}

function MatchDetailPage({ liveMatchIds }) {
  const navigate = useNavigate();
  const { matchid } = useParams();
  return (
    <MatchDetail
      matchid={matchid}
      isLive={liveMatchIds.has(Number(matchid))}
      onBack={() => navigate(-1)}
      onSelectPlayer={(id) => navigate(`/stats/players/${id}`)}
      onSelectH2H={(a, b) => navigate(`/stats/h2h/${a}/${b}`)}
    />
  );
}

function H2HPage() {
  const navigate = useNavigate();
  const { steamA, steamB } = useParams();
  return (
    <HeadToHead
      steamA={steamA}
      steamB={steamB}
      onBack={() => navigate(-1)}
      onSelectPlayer={(id) => navigate(`/stats/players/${id}`)}
      onSelectMatch={(id) => navigate(`/stats/matches/${id}`)}
    />
  );
}

function MatchesPage({ liveMatchIds }) {
  const navigate = useNavigate();
  return <MatchesList onSelectMatch={(id) => navigate(`/stats/matches/${id}`)} liveMatchIds={liveMatchIds} />;
}

function HighlightsPage() {
  const navigate = useNavigate();
  return <HighlightsList onSelectMatch={(id) => navigate(`/stats/matches/${id}`)} />;
}

function VetoPage() {
  const { matchid } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [vetoActive, setVetoActive] = useState(null);
  const [tournamentId, setTournamentId] = useState(null);

  const getRedirectPath = useCallback((tid) => {
    return tid ? `/tournaments/${tid}` : `/lobby/${matchid}`;
  }, [matchid]);

  // Check tournament info for this match
  useEffect(() => {
    fetch(`${API_URL}/api/tournaments/by-match/${matchid}/`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.tournament_id) setTournamentId(data.tournament_id); })
      .catch(() => {});
  }, [matchid, getAuthHeaders]);

  // Check if veto is still active — redirect if already complete
  useEffect(() => {
    fetch(`${API_URL}/api/veto/${matchid}/state`, { headers: getAuthHeaders() })
      .then(r => {
        if (!r.ok) { navigate(getRedirectPath(tournamentId), { replace: true }); return null; }
        return r.json();
      })
      .then(data => {
        if (!data) return;
        if (data.phase === 'complete' || data.current_round >= data.rounds?.length) {
          navigate(getRedirectPath(tournamentId), { replace: true });
        } else {
          setVetoActive(true);
        }
      })
      .catch(() => navigate(getRedirectPath(tournamentId), { replace: true }));
  }, [matchid, getAuthHeaders, navigate, tournamentId, getRedirectPath]);

  if (!vetoActive) {
    return <div className="veto-page"><div className="loading-screen"><div className="loader" /></div></div>;
  }

  return (
    <div className="veto-page">
      <VetoFlow
        matchId={parseInt(matchid)}
        onComplete={() => navigate(getRedirectPath(tournamentId), { replace: true })}
      />
    </div>
  );
}

/**
 * Dropdown navigation item that groups related routes under a single trigger.
 * Opens on hover (desktop) and click (tablet/touch).
 *
 * On phones (≤760px) the trigger/dropdown is hidden via CSS and the
 * `.nav-dropdown-mobile` section renders the same links expanded inline in
 * the hamburger drawer instead — a labeled group with full-width tap targets,
 * so no second tap is needed to reveal the items.
 */
function NavDropdown({ label, items }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const isChildActive = items.some(item =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)
  );

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div
      className={`nav-dropdown${open ? ' open' : ''}`}
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className={`nav-dropdown-trigger${isChildActive ? ' active' : ''}`}
        onClick={() => setOpen(prev => !prev)}
      >
        {label}
        <span className="nav-dropdown-arrow">&#9662;</span>
      </button>
      {open && (
        <div className="nav-dropdown-menu">
          {items.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-dropdown-item${isActive ? ' active' : ''}`}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
      <div className="nav-dropdown-mobile">
        <span className="nav-dropdown-mobile-label">{label}</span>
        {items.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `nav-dropdown-item${isActive ? ' active' : ''}`}
          >
            {item.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function AppContent() {
  const { t } = useTranslation();
  const liveMatches = useLiveMatches();
  const hasLiveMatch = liveMatches && liveMatches.length > 0;
  const liveMatchIds = useMemo(
    () => new Set((liveMatches || []).map(m => m.matchid)),
    [liveMatches]
  );

  const { user, getAuthHeaders } = useAuth();
  const { canCreate, guildId, features } = usePermissions();
  const { eligible: canSuggestMap } = useCanSuggestMap();
  const { count: openSlotsCount } = useOpenSlotsCount();
  const { hasAccess: hasOverwatchAccess } = useOverwatchAccess();
  const { activeMatch } = useActiveMatch(guildId);

  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Close the mobile drawer whenever the route changes (covers links,
  // dropdown items and programmatic navigation).
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  const [readyCheck, setReadyCheck] = useState(null);
  // Global accept modal — owns state for AcceptMatchBanner so the modal
  // shows on every route, not only /queue. `key` forces AcceptMatchBanner
  // to remount on a fresh match_found so its countdown effect re-runs even
  // when acceptTimeout is unchanged.
  const [acceptModal, setAcceptModal] = useState(null);
  const [acceptTimeoutCfg, setAcceptTimeoutCfg] = useState(0);
  // Separate timeout for /create-match accept modal. Driven by the
  // CREATE_MATCH_ACCEPT_TIMEOUT SiteConfig key, admin-editable via the
  // stats-page admin panel. Independent from queue accept timeout because
  // the two flows have different UX and target different audiences.
  const [createMatchAcceptTimeoutCfg, setCreateMatchAcceptTimeoutCfg] = useState(20);
  // Global accept modal for /create-match flow (separate from queue accept
  // because the accept POST goes to a different endpoint:
  // /api/web/matches/{id}/accept vs /api/queue/{gid}/accept). Mirrors the
  // queue-modal lifecycle so a match created via web pops up on every route.
  const [matchAcceptModal, setMatchAcceptModal] = useState(null);
  // Mirror of matchAcceptModal so event handlers can check "is this event
  // for my modal?" without depending on state inside the callback (which
  // would re-bind useQueueWebSocket on every modal change).
  const matchAcceptModalRef = useRef(null);
  matchAcceptModalRef.current = matchAcceptModal;

  const handleReadyCheck = useCallback((data) => setReadyCheck(data), []);
  const handleReadyCheckFinalized = useCallback(() => setReadyCheck(null), []);

  const handleMatchFound = useCallback((data) => {
    if (!user?.discord_id || !data.player_ids?.includes(user.discord_id)) return;
    const statuses = {};
    (data.players || []).forEach(p => { statuses[p.discord_id] = 'pending'; });
    setAcceptModal({
      players: data.players || [],
      statuses,
      acceptTimeout: acceptTimeoutCfg,
      key: Date.now(),
    });
  }, [user?.discord_id, acceptTimeoutCfg]);

  const handleAcceptUpdate = useCallback((data) => {
    setAcceptModal(prev => prev ? {
      ...prev,
      players: data.players || prev.players,
      statuses: data.statuses || prev.statuses,
    } : null);
  }, []);

  const handleMatchCancelled = useCallback(() => setAcceptModal(null), []);
  const handleAllAccepted = useCallback((data) => {
    setAcceptModal(null);
    // Queue autostart finished: the match was created server-side, so every
    // player lands in the lobby where veto begins. Mirrors the navigate on
    // all_accepted in the /create-match flow (handleMatchAcceptUpdate).
    if (data?.match_id) navigate(`/lobby/${data.match_id}`);
  }, [navigate]);

  // /create-match accept flow. match_created fires when a user creates a
  // match via /create — we show the global modal to every player in the
  // invite list (including creator, who is auto-accepted server-side and
  // sees "väntar på andra"). If a different match modal is already active,
  // ignore the new event so the in-flight accept window isn't yanked out
  // from under the user.
  const handleMatchCreatedPendingAccept = useCallback((data) => {
    if (!user?.discord_id) return;
    const myId = String(user.discord_id);
    const inMatch = (data?.player_ids || []).some((p) => String(p) === myId);
    if (!inMatch || !data.pending_accept) return;
    setMatchAcceptModal((prev) => {
      if (prev) return prev; // already showing a modal — don't replace it
      return {
        matchId: data.match_id,
        statuses: data.statuses || {},
        players: data.players || [],
        acceptTimeout: createMatchAcceptTimeoutCfg,
        key: Date.now(),
      };
    });
  }, [user?.discord_id, createMatchAcceptTimeoutCfg]);

  // Side effect (navigate) lives outside the setState updater; reading
  // current state via the ref keeps the handler stable and lets us check
  // "is this event for the modal I'm currently showing?" without putting
  // matchAcceptModal in useCallback deps (which would re-bind handlers).
  const handleMatchAcceptUpdate = useCallback((data) => {
    const current = matchAcceptModalRef.current;
    if (!current || String(current.matchId) !== String(data.match_id)) return;
    if (data.cancelled || data.all_accepted) {
      setMatchAcceptModal(null);
      if (data.all_accepted) navigate(`/lobby/${data.match_id}`);
    } else {
      setMatchAcceptModal((prev) => prev ? { ...prev, statuses: data.statuses || prev.statuses } : prev);
    }
  }, [navigate]);

  const handleGlobalMatchCancelled = useCallback((data) => {
    setMatchAcceptModal((prev) => {
      if (!prev || String(prev.matchId) !== String(data.match_id)) return prev;
      return null;
    });
  }, []);

  useQueueWebSocket(guildId, {
    onReadyCheck: handleReadyCheck,
    onReadyCheckFinalized: handleReadyCheckFinalized,
    onMatchFound: handleMatchFound,
    onAcceptUpdate: handleAcceptUpdate,
    onMatchCancelled: handleMatchCancelled,
    onAllAccepted: handleAllAccepted,
    onMatchCreatedPendingAccept: handleMatchCreatedPendingAccept,
    onMatchAcceptUpdate: handleMatchAcceptUpdate,
    onAnyMatchCancelled: handleGlobalMatchCancelled,
  });

  // Recover accept state on mount / reload — if the queue is locked and the
  // user is in the active accept_check, restore the modal so a refresh on
  // any route doesn't lose the in-progress accept window.
  useEffect(() => {
    if (!isHybridMode || !guildId || !user?.discord_id) return;
    let cancelled = false;
    Promise.all([
      fetch(`${API_URL}/api/web/config`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API_URL}/api/queue/${guildId}`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([cfg, queue]) => {
      if (cancelled) return;
      const timeout = cfg?.queue_accept_timeout || 0;
      setAcceptTimeoutCfg(timeout);
      const createTimeout = cfg?.create_match_accept_timeout;
      if (typeof createTimeout === 'number') {
        setCreateMatchAcceptTimeoutCfg(createTimeout);
      }
      const myId = String(user.discord_id);
      if (queue?.locked && queue?.accept_check && myId in queue.accept_check) {
        const players = Array.isArray(queue.accept_players) && queue.accept_players.length
          ? queue.accept_players
          : Object.keys(queue.accept_check).map(id => ({ discord_id: id, username: id }));
        // Resume the countdown from the backend's remaining TTL so a refresh
        // doesn't reset the timer to the full window. Mirrors the
        // /create-match recovery below. Clamp to the configured timeout
        // (the backend already stripped its expiry buffer).
        const rawTtl = typeof queue.accept_ttl === 'number' && queue.accept_ttl > 0
          ? queue.accept_ttl
          : timeout;
        const remaining = timeout > 0 ? Math.min(rawTtl, timeout) : rawTtl;
        setAcceptModal({
          players,
          statuses: queue.accept_check,
          acceptTimeout: remaining,
          key: Date.now(),
        });
      }
    });
    return () => { cancelled = true; };
  }, [guildId, user?.discord_id]);

  const handleAccept = useCallback(async () => {
    if (!guildId) return;
    try {
      await fetch(`${API_URL}/api/queue/${guildId}/accept`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (user?.discord_id) {
        setAcceptModal(prev => prev ? {
          ...prev,
          statuses: { ...prev.statuses, [user.discord_id]: 'accepted' },
        } : null);
      }
    } catch (e) {}
  }, [guildId, getAuthHeaders, user?.discord_id]);

  const handleDecline = useCallback(async () => {
    if (!guildId) {
      setAcceptModal(null);
      return;
    }
    try {
      await fetch(`${API_URL}/api/queue/${guildId}/decline`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
    } catch (e) {}
    setAcceptModal(null);
  }, [guildId, getAuthHeaders]);

  // Recover pending match-accept state on mount / reload. Mirrors the queue
  // accept recovery above but for /create-match flow: scans active
  // match_accept:* hashes server-side and pops the modal if this user is
  // in one. Without this, a refresh during the 2-min accept window leaves
  // the user with no modal at all.
  useEffect(() => {
    if (!isHybridMode || !user?.discord_id) return;
    let cancelled = false;
    fetch(`${API_URL}/api/web/my-pending-match-accepts?discord_id=${user.discord_id}`)
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => [])
      .then((list) => {
        if (cancelled || !Array.isArray(list) || list.length === 0) return;
        const entry = list[0];
        // Prefer the backend's remaining TTL so the countdown resumes from
        // where it actually is (server is the source of truth). Fall back
        // to the configured timeout only if the backend doesn't provide
        // one (older API, or key already expired).
        //
        // Clamp to the admin-configured timeout: the backend Redis TTL has
        // a +10s buffer so late decline-calls don't race against expiry,
        // but that buffer must not be visible to the user — the countdown
        // they see has to match the admin-set value on every refresh.
        //
        // "No limit" (config 0): the backend still sets a 600s Redis safety
        // TTL, but that is a backstop, not a user-facing timer. Surfacing it
        // made the countdown jump from the initial 20 up to ~590 on recovery.
        // Show no countdown at all in that case (0 → AcceptMatchBanner renders
        // no timer) and rely on the manual accept/cancel buttons instead.
        const rawTtl = typeof entry.ttl === 'number' && entry.ttl > 0
          ? entry.ttl
          : createMatchAcceptTimeoutCfg;
        const remaining = createMatchAcceptTimeoutCfg > 0
          ? Math.min(rawTtl, createMatchAcceptTimeoutCfg)
          : 0;
        setMatchAcceptModal({
          matchId: entry.match_id,
          statuses: entry.statuses || {},
          players: entry.players || [],
          acceptTimeout: remaining,
          key: Date.now(),
        });
      });
    return () => { cancelled = true; };
  }, [user?.discord_id, createMatchAcceptTimeoutCfg]);

  const handleMatchAccept = useCallback(async () => {
    const matchId = matchAcceptModal?.matchId;
    if (!matchId) return;
    try {
      await fetch(`${API_URL}/api/web/matches/${matchId}/accept`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (user?.discord_id) {
        setMatchAcceptModal((prev) => prev ? {
          ...prev,
          statuses: { ...prev.statuses, [user.discord_id]: 'accepted' },
        } : null);
      }
    } catch (e) {}
  }, [matchAcceptModal?.matchId, getAuthHeaders, user?.discord_id]);

  const handleMatchDecline = useCallback(async () => {
    const matchId = matchAcceptModal?.matchId;
    if (!matchId) {
      setMatchAcceptModal(null);
      return;
    }
    try {
      await fetch(`${API_URL}/api/web/matches/${matchId}/decline`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
    } catch (e) {}
    setMatchAcceptModal(null);
  }, [matchAcceptModal?.matchId, getAuthHeaders]);

  const presence = usePresence();

  // Shared trigger so CreateMatch (and any future caller) can open the
  // accept modal locally right after a successful POST, without depending
  // on the match_created WebSocket roundtrip. The WS path is still live as
  // a backstop for OTHER players who didn't originate the create call.
  const matchAcceptModalCtx = useMemo(() => ({
    openMatchAcceptModal: (data) => {
      setMatchAcceptModal((prev) => {
        if (prev) return prev; // already open — don't replace
        return {
          matchId: data.matchId ?? data.match_id,
          statuses: data.statuses || {},
          players: data.players || [],
          acceptTimeout:
            typeof data.acceptTimeout === 'number'
              ? data.acceptTimeout
              : createMatchAcceptTimeoutCfg,
          key: Date.now(),
        };
      });
    },
    closeMatchAcceptModal: () => setMatchAcceptModal(null),
  }), [createMatchAcceptTimeoutCfg]);

  return (
    <MatchAcceptModalProvider value={matchAcceptModalCtx}>
    <div className="app">
      <header className="header">
        <div className="header-top">
          <div className="logo">
            <span className="logo-icon">🎮</span>
            <span className="logo-text">PARTYFABRIKEN CS2</span>
          </div>
          <div className="header-right">
            <button
              type="button"
              className="nav-search-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('open-cmdk'))}
              title={t('nav.searchTitle')}
            >
              🔎<span className="nav-search-hint">{t('nav.search')}</span><kbd>⌘K</kbd>
            </button>
            {isHybridMode && (
              <>
                {/* Saldot och notisklockan ligger i headern, utanför
                    ErrorBoundaryn runt <Routes>. Utan egen boundary hade en
                    renderkrasch i poängekonomin svartnat hela appen — inklusive
                    kön och vetot. UserMenu lämnas oinnesluten: utan den kan man
                    inte logga in eller ut, så där är felkortet rätt utfall. */}
                <FeatureBoundary name="WalletChip">
                  <WalletChip />
                </FeatureBoundary>
                <FeatureBoundary name="NotificationBell">
                  <NotificationBell />
                </FeatureBoundary>
                <UserMenu />
              </>
            )}
            <button
              type="button"
              className="nav-hamburger"
              aria-label={t('nav.menu')}
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen((o) => !o)}
            >
              {mobileNavOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>
        <nav className={`nav ${mobileNavOpen ? 'nav-mobile-open' : ''}`}>
          {/* Phone drawer: account actions live here instead of in header-top,
              where login buttons / user menu don't fit and used to push the
              whole page wider than the viewport. Hidden on desktop via CSS. */}
          {isHybridMode && (
            <div className="nav-mobile-account">
              <UserMenu />
            </div>
          )}
          <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>
            {t('nav.home')}
          </NavLink>
          <NavLink to="/news" className={({ isActive }) => isActive ? 'active' : ''}>
            {t('nav.news')}
          </NavLink>
          <NavDropdown
            label={t('nav.stats')}
            items={[
              { to: '/stats/leaderboard', label: t('nav.leaderboard') },
              { to: '/stats/players', label: t('nav.players') },
              { to: '/stats/matches', label: t('nav.matches') },
              { to: '/stats/maps', label: t('nav.maps') },
              { to: '/stats/medaljer', label: t('nav.medals') },
            ]}
          />
          {features.SHOP_ENABLED !== false && (
            <NavLink to="/shop" className={({ isActive }) => isActive ? 'active' : ''}>
              {/* Present, inte kundvagn: sidan låser upp saker med fiktiva
                  poäng och säljer ingenting. Se PointsDisclaimer. */}
              🎁 {t('nav.shop')}
            </NavLink>
          )}
          {features.MARKETPLACE_ENABLED === true && (
            <NavLink to="/marketplace" className={({ isActive }) => isActive ? 'active' : ''}>
              🤝 {t('nav.marketplace')}
            </NavLink>
          )}
          <NavLink to="/tournaments" className={({ isActive }) => isActive ? 'active' : ''}>
            {t('nav.tournaments')}
          </NavLink>
          <NavLink to="/leagues" className={({ isActive }) => isActive ? 'active' : ''}>
            {t('nav.leagues')}
          </NavLink>
          {isHybridMode && (
            <NavDropdown
              label={t('nav.play')}
              items={[
                { to: '/queue', label: t('nav.queue') },
                { to: '/open-slots', label: t('nav.openSlots') },
                ...(canCreate ? [{ to: '/create', label: t('nav.createMatch') }] : []),
                ...(canSuggestMap ? [{ to: '/suggest-map', label: t('nav.suggestMap') }] : []),
              ]}
            />
          )}
          <NavLink to="/highlights" className={({ isActive }) => `highlights-tab ${isActive ? 'active' : ''}`}>
            <span className="nav-highlight-icon">🎬</span>
            {t('nav.highlights')}
          </NavLink>
          <NavLink to="/live" className={({ isActive }) => `live-tab ${isActive ? 'active' : ''} ${hasLiveMatch ? 'has-live' : ''}`}>
            <span className={`nav-live-dot ${hasLiveMatch ? 'active' : ''}`}></span>
            {t('nav.live')}
          </NavLink>
          {isHybridMode && openSlotsCount > 0 && (
            <NavLink
              to="/open-slots"
              className={({ isActive }) => `open-slots-tab ${isActive ? 'active' : ''}`}
              title={t('openSlots.navBadgeTitle')}
            >
              <span className="open-slots-tab-dot" />
              {t('openSlots.navBadge', { count: openSlotsCount })}
            </NavLink>
          )}
          {isHybridMode && activeMatch && (
            <NavLink
              to={`/lobby/${activeMatch.match_id}`}
              className={({ isActive }) => `active-match-tab status-${activeMatch.status?.toLowerCase()} ${isActive ? 'active' : ''}`}
              title={`${activeMatch.team1_name} vs ${activeMatch.team2_name}`}
            >
              <span className="active-match-tab-dot" />
              {activeMatch.status === 'LIVE' ? t('nav.inMatch') : activeMatch.status === 'STARTED' ? t('nav.ready') : t('nav.match')}
            </NavLink>
          )}
          {features.SHOW_VETO_DEMO && (
            <NavLink to="/veto-demo" className={({ isActive }) => isActive ? 'active' : ''}>
              Veto Demo
            </NavLink>
          )}
          {features.SHOW_QUEUE_DEMO && (
            <NavLink to="/queue-demo" className={({ isActive }) => isActive ? 'active' : ''}>
              Queue Demo
            </NavLink>
          )}
          {features.SHOW_CREATE_DEMO && (
            <NavLink to="/create-demo" className={({ isActive }) => isActive ? 'active' : ''}>
              Create Demo
            </NavLink>
          )}
          {hasOverwatchAccess && (
            <NavLink to="/overwatch" className={({ isActive }) => isActive ? 'active' : ''}>
              🛡️ Overwatch
            </NavLink>
          )}
        </nav>
      </header>

      <DocumentTitle />
      {/* Notistoasten, paletten och statusbannern renderas utanför
          route-boundaryn. Ingen av dem behövs för att spela match, så de får
          försvinna tyst i stället för att ta med sig kön eller vetot. */}
      <FeatureBoundary name="NotificationToast">
        <NotificationToast />
      </FeatureBoundary>
      <FeatureBoundary name="CommandPalette">
        <CommandPalette />
      </FeatureBoundary>

      <main className="main-content">
        <FeatureBoundary name="StatusNoticeBanner">
          <StatusNoticeBanner />
        </FeatureBoundary>
        {/* key på pathname: utan den ligger hasError kvar efter en krasch, så
            ett fel på /shop hade visat felkortet även när man navigerade
            vidare till /queue — man tvingades ladda om sidan för att komma
            tillbaka till matchflödet. */}
        <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/stats/leaderboard" element={<LeaderboardsPage />} />
          <Route path="/stats/maps" element={<MapStats />} />
          {isHybridMode && <Route path="/suggest-map" element={<SuggestMap />} />}
          <Route path="/stats/medaljer" element={<MedalsPage />} />
          <Route path="/stats/season" element={<SeasonOverview />} />
          <Route path="/stats/players" element={<PlayersPage />} />
          <Route path="/stats/players/:steamid64" element={<PlayerProfilePage />} />
          <Route path="/stats/matches" element={<MatchesPage liveMatchIds={liveMatchIds} />} />
          <Route path="/stats/matches/:matchid" element={<MatchDetailPage liveMatchIds={liveMatchIds} />} />
          <Route path="/stats/h2h/:steamA/:steamB" element={<H2HPage />} />
          <Route path="/highlights" element={<HighlightsPage />} />
          <Route path="/tournaments" element={<TournamentList />} />
          <Route path="/tournaments/:id" element={<TournamentDetail />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:slug" element={<NewsDetail />} />
          {features.SHOP_ENABLED !== false && <Route path="/shop" element={<ShopPage />} />}
          {features.MARKETPLACE_ENABLED === true && <Route path="/marketplace" element={<MarketplacePage />} />}
          <Route path="/leagues" element={<LeagueList />} />
          <Route path="/leagues/teams" element={<LeagueTeams />} />
          {isHybridMode && <Route path="/leagues/teams/new" element={<CreateLeagueTeam />} />}
          <Route path="/leagues/teams/:teamId" element={<LeagueTeamDetail />} />
          <Route path="/leagues/me" element={<MyLeagues />} />
          <Route path="/leagues/:id" element={<LeagueDetail />} />
          {isHybridMode && <Route path="/queue" element={<Queue />} />}
          {isHybridMode && <Route path="/open-slots" element={<OpenSlots />} />}
          {isHybridMode && canCreate && <Route path="/create" element={<CreateMatch />} />}
          {isHybridMode && <Route path="/lobby/:matchid" element={<CreateMatch />} />}
          {isHybridMode && <Route path="/veto/:matchid" element={<VetoPage />} />}
          {isHybridMode && <Route path="/profile" element={<Profile />} />}
          {isHybridMode && <Route path="/webadmin/*" element={<AdminLayout />} />}
          <Route path="/live" element={
            <LiveMatch liveMatches={liveMatches} />
          } />
          {features.SHOW_VETO_DEMO && <Route path="/veto-demo" element={<VetoDemo />} />}
          {features.SHOW_QUEUE_DEMO && <Route path="/queue-demo" element={<QueueDemo />} />}
          {features.SHOW_CREATE_DEMO && <Route path="/create-demo" element={<CreateMatchDemo />} />}
          <Route path="/overwatch" element={<OverwatchDashboard />} />
          <Route path="/overwatch/admin" element={<OverwatchAdmin />} />
          <Route path="/overwatch/report/:reportId" element={<OverwatchReport />} />
          <Route path="/overwatch/player/:steamId" element={<OverwatchPlayer />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/pending" element={<PendingApproval />} />
        </Routes>
        </ErrorBoundary>
      </main>

      {/* Temalabbets granskningsbanderoll. Renderas globalt och inte i
          adminpanelen — hela poängen är att kunna gå runt på riktiga sidor i
          temat. Returnerar null när inget granskas. */}
      <ThemePreviewBanner />

      {/* Installationserbjudandet. Globalt och inte bundet till isHybridMode —
          poängen är att appen ska gå att installera var man än råkar landa,
          och komponenten döljer sig själv när den redan körs installerad. */}
      <InstallPrompt />

      {isHybridMode && <OnlineSidebar {...presence} />}

      {isHybridMode && acceptModal && (
        <AcceptMatchBanner
          key={acceptModal.key}
          players={acceptModal.players}
          statuses={acceptModal.statuses}
          acceptTimeout={acceptModal.acceptTimeout}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      )}

      {isHybridMode && matchAcceptModal && !acceptModal && (
        <AcceptMatchBanner
          key={matchAcceptModal.key}
          players={matchAcceptModal.players}
          statuses={matchAcceptModal.statuses}
          acceptTimeout={matchAcceptModal.acceptTimeout}
          onAccept={handleMatchAccept}
          onDecline={handleMatchDecline}
          allowCancelWhenAccepted
        />
      )}

      {isHybridMode && readyCheck && (
        <PlayAgainBanner
          guildId={guildId}
          matchId={readyCheck.match_id}
          timeout={readyCheck.timeout}
          onDismiss={handleReadyCheckFinalized}
        />
      )}

      {isHybridMode && activeMatch && !readyCheck && (
        <ActiveMatchBanner
          key={`${activeMatch.match_id}-${activeMatch.status}`}
          matchData={activeMatch}
        />
      )}

      <footer className="footer">
        <p>CS2 Stats Tracker - Powered by MatchZy</p>
      </footer>

      {!isHybridMode && <ChatWidget />}
    </div>
    </MatchAcceptModalProvider>
  );
}

function App() {
  return (
    <BrowserRouter basename="/">
      <AuthProvider>
        <SettingsProvider>
          <NotificationProvider>
            <AppContent />
          </NotificationProvider>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
